import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaPlus, FaArrowLeft } from 'react-icons/fa';
import { useTitle } from '../../../shared/context/TitleContext';
import { fetchPermissions, createRole, assignPermissionsToRole, clearError } from '../../../redux/Slices/PermissionsSlice';
import { useNavigate } from 'react-router-dom';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import PermissionGate from '../../../shared/components/PermissionGate';

const AddRole = () => {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { permissions, loading, error, success } = useSelector(state => state.permissions);

  const [roleForm, setRoleForm] = useState({
    name_en: '',
    name_ar: '',
    permissions: []
  });

  useEffect(() => {
    setTitle('إضافة دور جديد');
    dispatch(clearError());
    dispatch(fetchPermissions());
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      showSuccessToast(success, {
        position: "top-right",
        rtl: true,
        autoClose: 2000,
        onClose: () => {
          navigate('/admin-permissions');
        }
      });
    }
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!roleForm.name_en.trim() || !roleForm.name_ar.trim()) {
      showErrorToast('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    try {
      // إنشاء الدور أولاً
      const roleData = {
        name_en: roleForm.name_en,
        name_ar: roleForm.name_ar
      };
      
      const createResult = await dispatch(createRole(roleData)).unwrap();
      
      if (createResult.data && createResult.data.id && roleForm.permissions.length > 0) {
        // ربط الصلاحيات بالدور
        await dispatch(assignPermissionsToRole({
          roleId: createResult.data.id,
          permissions: roleForm.permissions
        })).unwrap();
      }
    } catch (error) {
    }
  };

  const handlePermissionToggle = (permission) => {
    const updatedPermissions = roleForm.permissions.includes(permission)
      ? roleForm.permissions.filter(p => p !== permission)
      : [...roleForm.permissions, permission];
    
    setRoleForm({
      ...roleForm,
      permissions: updatedPermissions
    });
  };

  const handleSelectAll = () => {
    if (roleForm.permissions.length === permissions.length) {
      setRoleForm({
        ...roleForm,
        permissions: []
      });
    } else {
      setRoleForm({
        ...roleForm,
        permissions: permissions.map(p => p.name)
      });
    }
  };

  return (
    <div className="main_content">
      <div className="dashboard-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="d-flex justify-content-between align-items-center mb-4"
        >
          <div className="d-flex align-items-center">
            <button
              className="btn btn-outline-secondary mx-3"
              onClick={() => navigate('/admin-permissions')}
              style={{
                borderColor: 'rgb(24, 65, 148)',
                color: 'rgb(24, 65, 148)'
              }}
            >
              <FaArrowLeft className="mx-2" />
              العودة
            </button>
            <h4 className="fw-bold text-dark mb-0">إضافة دور جديد</h4>
          </div>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="card shadow-sm"
        >
          <div className="card-body p-4">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">اسم الدور (إنجليزي)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={roleForm.name_en}
                      onChange={(e) => setRoleForm({ ...roleForm, name_en: e.target.value })}
                      required
                      placeholder="أدخل اسم الدور بالإنجليزية"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">اسم الدور (عربي)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={roleForm.name_ar}
                      onChange={(e) => setRoleForm({ ...roleForm, name_ar: e.target.value })}
                      required
                      placeholder="أدخل اسم الدور بالعربية"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <label className="form-label fw-semibold mb-0">الصلاحيات</label>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleSelectAll}
                    style={{
                      borderColor: 'rgb(24, 65, 148)',
                      color: 'rgb(24, 65, 148)'
                    }}
                  >
                    {roleForm.permissions.length === permissions.length ? 'إلغاء الكل' : 'تحديد الكل'}
                  </button>
                </div>
                
                <div className="row" style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '8px', padding: '15px' }}>
                  {permissions.map((permission) => (
                    <div key={permission.id} className="col-md-6 mb-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`permission-${permission.id}`}
                          checked={roleForm.permissions.includes(permission.name)}
                          onChange={() => handlePermissionToggle(permission.name)}
                        />
                        <label className="form-check-label" htmlFor={`permission-${permission.id}`}>
                          {permission.name}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="d-flex justify-content-end gap-3">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate('/admin-permissions')}
                  style={{
                    backgroundColor: 'rgb(24, 65, 148)',
                    borderColor: 'rgb(24, 65, 148)',
                    color: 'white'
                  }}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                  style={{
                    backgroundColor: 'rgb(24, 65, 148)',
                    borderColor: 'rgb(24, 65, 148)'
                  }}
                >
                  {loading ? 'جاري الإنشاء...' : 'إنشاء الدور'}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AddRole;
